import {
  useEffect,
  useState,
  Fragment,
  useRef,
  useMemo,
  useCallback,
} from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { Card } from "reactstrap";
import Loading from "../../components/utils/Loading";
import CameraRow from "./CameraRow";
import Pagination from "../../components/utils/Pagination";
import CameraHeader from "./CameraHeader";
import { useTranslation } from "react-i18next";
import CameraDetailModal from "./CameraDetailModal";
import { formattedImageName } from "../../components/formatted/FormattedAzureUrl";
import EmptyState from "../../components/EmptyState";
import StandardTable from "../../ui/components/StandardTable";
import StandardTableFilterDropdown from "../../ui/components/StandardTableFilterDropdown";

const Camera = () => {
  const { t } = useTranslation();
  const API = process.env.REACT_APP_API_BACKEND;
  const token = Cookies.get("userToken");

  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalEntry, setTotalEntry] = useState();
  const [totalEntryWithoutFilter, setTotalEntryWithoutFilter] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordPerPage, setRecordPerPage] = useState(10);
  const [resultPage, setResultPage] = useState(0);
  const [isDeleted, setIsDeleted] = useState(false);
  const [sortByField, setSortByField] = useState("name");
  const [orderSortByField, setOrderSortByField] = useState("1");
  const [keywordFilter, setKeywordFilter] = useState("");
  const [isModalView, setIsModalView] = useState(false);
  const [showedCamera, setShowedCamera] = useState(null);
  const [mergedCamera, setMergedCamera] = useState({});
  const [listAddressFilter, setListAddressFilter] = useState({});
  const [addressKeys, setAddressKeys] = useState([]);
  const [batteryCache, setBatteryCache] = useState({});
  const [initialCamerasLoaded, setInitialCamerasLoaded] = useState(false);
  const loadedBatteriesRef = useRef(new Set());
  const loadingBatteriesRef = useRef(new Set());
  const hasCameras = Array.isArray(tableData) && tableData.length > 0;
  const hasAnyCameras = totalEntryWithoutFilter > 0;
  const hasActiveFilters = Boolean(
    keywordFilter?.trim() ||
      (listAddressFilter?.selectedList &&
        listAddressFilter.selectedList.length > 0),
  );
  const showEmptyState = !hasAnyCameras && !hasActiveFilters;

  useEffect(() => {
    const fetchData = async () => {
      await axios
        .all([
          axios.get(`${API}cameras`, {
            headers: { authorization: `Bearer ${token}` },
            params: {
              all: false,
              keywordFilter: keywordFilter,
              sortByField: sortByField,
              orderSortByField: orderSortByField,
              batteryLevelFilter: [0, 100],
              startDateFilter: null,
              stopDateFilter: null,
              listAddressFilter: listAddressFilter,
              page: currentPage,
              limit: recordPerPage,
            },
          }),
        ])
        .then(
          axios.spread((responseCameras) => {
            if (responseCameras.status === 200) {
              let cameras = (responseCameras.data.dataCamerasList || []).filter(
                (camera) =>
                  camera?.status !== "virtual" &&
                  !String(camera?.name || "")
                    .toLowerCase()
                    .includes("mobile"),
              );

              // Filtrer par adresse côté client si un filtre est sélectionné
              if (
                listAddressFilter?.selectedList &&
                listAddressFilter.selectedList.length > 0
              ) {
                const selectedAddress = listAddressFilter.selectedList[0].value;
                cameras = cameras.filter((camera) => {
                  if (camera.address && camera.address.length > 0) {
                    return camera.address[0].name === selectedAddress;
                  }
                  return false;
                });
              }

              setTableData(cameras);
              const apiCount = cameras.length;
              setTotalEntry(apiCount);
              setResultPage(Math.max(1, Math.ceil(apiCount / recordPerPage)));
              if (!hasActiveFilters) {
                setTotalEntryWithoutFilter(apiCount);
              }
              setMergedCamera(responseCameras.data.mergedCamera);
              // Récupérer les adresses uniques depuis toutes les caméras (pas seulement celles filtrées)
              const uniqueAddresses = new Map();
              cameras.forEach((camera) => {
                if (camera.address && camera.address.length > 0) {
                  const addressName = camera.address[0].name;
                  if (addressName && !uniqueAddresses.has(addressName)) {
                    uniqueAddresses.set(addressName, {
                      value: addressName,
                      label: addressName,
                    });
                  }
                }
              });
              setAddressKeys(Array.from(uniqueAddresses.values()));
              setIsLoading(false);
              setIsDeleted(false);

              // Marquer que les caméras initiales sont chargées
              if (!initialCamerasLoaded) {
                setInitialCamerasLoaded(true);
              }
            }
          }),
        );
    };
    fetchData();
  }, [
    API,
    keywordFilter,
    currentPage,
    recordPerPage,
    token,
    isDeleted,
    sortByField,
    orderSortByField,
    listAddressFilter,
    hasActiveFilters,
    initialCamerasLoaded,
  ]);

  const getCameraTimestamp = useCallback((camera) => {
    const match = camera.image_url?.match(/pictures\/(.*?)-camera/);
    if (match?.[1]) {
      return new Date(match[1].replace(/_/g, ":") + "Z");
    }
    if (camera.lastUpdate) {
      return new Date(camera.lastUpdate);
    }
    return new Date(0);
  }, []);

  const filterCameras = useCallback(
    (datas) => {
      // Utiliser un Map pour préserver l'ordre d'insertion
      const filteredCameras = new Map();

      // Pour préserver l'ordre du serveur, on garde l'index original
      datas.forEach((camera, index) => {
        const baseId = camera._id.replace(/_[rl]$/, "");
        const timestamp = getCameraTimestamp(camera);

        if (
          !filteredCameras.has(baseId) ||
          timestamp > filteredCameras.get(baseId).timestamp
        ) {
          filteredCameras.set(baseId, {
            ...camera,
            timestamp,
            originalIndex: index,
          });
        }
      });

      // Convertir le Map en tableau
      const result = Array.from(filteredCameras.values());

      // Pour batteryLevel, préserver l'ordre du serveur en triant par originalIndex
      if (sortByField === "batteryLevel" && result.length > 0) {
        result.sort((a, b) => {
          return (a.originalIndex || 0) - (b.originalIndex || 0);
        });
      } else if (sortByField && result.length > 0) {
        // Pour les autres champs, trier côté client
        result.sort((a, b) => {
          let aValue, bValue;

          if (sortByField === "name") {
            aValue = a._id || "";
            bValue = b._id || "";
          } else if (sortByField === "lastScanDate") {
            aValue = a.timestamp || new Date(0);
            bValue = b.timestamp || new Date(0);
          } else {
            return 0;
          }

          if (typeof aValue === "string" && typeof bValue === "string") {
            return orderSortByField === "1"
              ? aValue.localeCompare(bValue)
              : bValue.localeCompare(aValue);
          } else if (aValue instanceof Date && bValue instanceof Date) {
            return orderSortByField === "1"
              ? aValue.getTime() - bValue.getTime()
              : bValue.getTime() - aValue.getTime();
          }

          return 0;
        });
      }

      return result;
    },
    [sortByField, orderSortByField, getCameraTimestamp],
  );

  const displayedCameras = useMemo(() => {
    return hasCameras ? filterCameras(tableData) : [];
  }, [hasCameras, tableData, filterCameras]);

  // Charger les batteries uniquement pour les caméras qui ne sont pas en cache
  useEffect(() => {
    if (!initialCamerasLoaded || !tableData.length) return;

    const loadBatteries = async () => {
      // Charger uniquement les batteries qui ne sont pas déjà chargées ou en cours de chargement
      const camerasToLoad = tableData.filter((camera) => {
        return (
          camera._id &&
          !loadedBatteriesRef.current.has(camera._id) &&
          !loadingBatteriesRef.current.has(camera._id)
        );
      });

      if (camerasToLoad.length === 0) {
        return;
      }

      // Marquer ces caméras comme en cours de chargement
      camerasToLoad.forEach((camera) => {
        loadingBatteriesRef.current.add(camera._id);
      });

      const batteryPromises = camerasToLoad.map(async (camera) => {
        let cameraId = camera._id;
        const imageName = formattedImageName(camera.image_url, cameraId);

        if (camera.isAkanthasDevice) {
          cameraId = camera._id.replace("_l", "").replace("_r", "");
        }

        try {
          const response = await axios.get(`${API}cameraLog/${cameraId}`, {
            headers: { authorization: `Bearer ${token}` },
            params: { imageName },
          });

          if (response.status === 200) {
            return { cameraId: camera._id, battery: response.data.battery };
          }
        } catch (err) {
          console.error(`Error loading battery for ${cameraId}:`, err);
        }
        return { cameraId: camera._id, battery: null };
      });

      const batteryResults = await Promise.all(batteryPromises);
      const newCache = {};
      batteryResults.forEach(({ cameraId, battery }) => {
        if (cameraId) {
          newCache[cameraId] = battery;
          loadedBatteriesRef.current.add(cameraId);
          loadingBatteriesRef.current.delete(cameraId);
        }
      });

      setBatteryCache((prev) => ({ ...prev, ...newCache }));
    };

    loadBatteries();
  }, [initialCamerasLoaded, tableData, API, token]);

  // Réinitialiser le cache de batteries uniquement lors d'une suppression
  useEffect(() => {
    if (isDeleted) {
      setBatteryCache({});
      loadedBatteriesRef.current.clear();
      loadingBatteriesRef.current.clear();
    }
  }, [isDeleted]);

  if (token) {
    return isLoading ? (
      <Loading color="#F5F5F5" />
    ) : (
      <div className="w-full">
        <Card className="table border-0 shadow-none bg-white rounded-lg">
          <StandardTable
            fluidColCount={showEmptyState ? 1 : 4}
            fixedColWidth={0}
            containerClassName={
              showEmptyState
                ? "table-standard-container--list-empty"
                : undefined
            }
            columnAlignments={
              showEmptyState ? ["left"] : ["left", "left", "left", "right"]
            }
            topBar={
              hasAnyCameras ? (
                <section className="table-standard-top-bar">
                  <div className="table-standard-top-bar-inner">
                    <div className="flex gap-2 items-center flex-wrap flex-1 min-w-0">
                      <div className="search-input-wrapper">
                        <i className="fas fa-search search-input-icon" />
                        <input
                          type="text"
                          className="search-input"
                          value={keywordFilter}
                          placeholder={`${t("Search")} (${t("ID")})`}
                          onChange={(event) =>
                            setKeywordFilter(event.target.value)
                          }
                        />
                      </div>
                      <StandardTableFilterDropdown
                        label={t("Address")}
                        value={
                          listAddressFilter?.selectedList?.[0]?.value || ""
                        }
                        options={addressKeys}
                        allLabel={t("All addresses")}
                        onChange={(nextValue) => {
                          if (!nextValue) {
                            setListAddressFilter({});
                            return;
                          }
                          const selected = addressKeys.find(
                            (address) => address.value === nextValue,
                          );
                          if (selected) {
                            setListAddressFilter({ selectedList: [selected] });
                          }
                        }}
                      />
                    </div>
                  </div>
                </section>
              ) : null
            }
            header={
              hasAnyCameras ? (
                <CameraHeader
                  onSort={(field, order) => {
                    setSortByField(field);
                    setOrderSortByField(order);
                  }}
                  sortByField={sortByField}
                  orderSortByField={orderSortByField}
                />
              ) : null
            }
            bottom={
              hasAnyCameras ? (
                <div className="table-standard-bottom table-pagination-bottom">
                  <Pagination
                    totalEntry={totalEntry}
                    recordPerPage={recordPerPage}
                    setRecordPerPage={setRecordPerPage}
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    resultPage={resultPage}
                    setResultPage={setResultPage}
                  />
                </div>
              ) : null
            }
          >
            {hasCameras ? (
              displayedCameras.map((camera, index) => (
                <Fragment key={camera._id || index}>
                  <CameraRow
                    data={camera}
                    battery={batteryCache[camera._id]}
                    setShowedCamera={setShowedCamera}
                    setIsModalView={setIsModalView}
                  />
                </Fragment>
              ))
            ) : hasAnyCameras && hasActiveFilters ? (
              <div className="table-standard-row table-standard-row--empty">
                <div
                  className="table-standard-row-cell"
                  style={{ gridColumn: "span 4" }}
                >
                  {t("No results match your search")}
                </div>
              </div>
            ) : (
              <EmptyState
                title={t("emptyState.noCamera")}
                description={t("emptyState.hintCamera")}
              />
            )}
          </StandardTable>
        </Card>
        {showedCamera !== null && (
          <CameraDetailModal
            setShowedCamera={setShowedCamera}
            data={showedCamera}
            setIsModalView={setIsModalView}
            isModalView={isModalView}
            mergedCamera={mergedCamera}
          />
        )}
      </div>
    );
  }
};

export default Camera;
